'use client';

/**
 * PaymentMethodsLogo Component
 *
 * Dynamically displays payment method logos based on available payment gateways.
 * Fetches available methods from Adyen via useGetPaymentGateways hook.
 *
 * Ported from breitling-storefront-next/src/components/ShoppingBag/PaymentMethodsLogo.tsx
 */

import { useMemo } from 'react';

import { useGetPaymentGateways } from '@/hooks/useGetPaymentGateways';
import { usePaymentMethodAvailability } from '@/hooks/usePaymentMethodAvailability';
import { PAYMENT_TYPES } from '@/lib/checkout/payments/types';

import { getPaymentMethodsLogos } from './paymentMethodsLogos';

import styles from './PaymentMethodsLogo.module.scss';

export interface PaymentMethodsLogoProps {
  /** Additional class name */
  className?: string;
}

/**
 * PaymentMethodsLogo - Displays payment method logos from Adyen gateway
 */
export function PaymentMethodsLogo({ className }: PaymentMethodsLogoProps) {
  const { gatewayConfigs, isLoading } = useGetPaymentGateways();
  const { isMethodAvailable } = usePaymentMethodAvailability();

  const isApplePayAvailable = isMethodAvailable(PAYMENT_TYPES.APPLE_PAY);

  const allPaymentMethods = useMemo(() => {
    if (!gatewayConfigs?.length) return [];
    return gatewayConfigs.flatMap((config) => config.paymentMethods || []);
  }, [gatewayConfigs]);

  const paymentLogos = useMemo(() => {
    return getPaymentMethodsLogos(allPaymentMethods, isApplePayAvailable, styles.logo);
  }, [allPaymentMethods, isApplePayAvailable]);

  if (isLoading) {
    return (
      <div className={`${styles.container} ${className || ''}`}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (!paymentLogos.length) {
    return null;
  }

  return (
    <div className={`${styles.container} ${className || ''}`} data-testid="PaymentMethodsLogo">
      {paymentLogos.map((logo) => logo.img)}
    </div>
  );
}
